/**
 * Permission Storage Service
 * CACHING ENABLED - Cache permissions for 10 minutes
 * Load on login and refresh every 10 minutes
 */

import type {
  PermissionGroup,
  UserPermission,
  PermissionConfigData,
  RolePermissionAssignment,
} from './api';

const PERMISSION_GROUPS_KEY = 'permission_groups';
const USER_PERMISSIONS_KEY = 'user_permissions';
const PERMISSION_TIMESTAMP_KEY = 'permission_timestamp';
const PERMISSION_USER_ID_KEY = 'permission_user_id';
const PERMISSION_TEAM_IDS_KEY = 'permission_team_ids';
const PERMISSION_CONFIG_KEY = 'permission_config_snapshot';
const ROLE_PERMISSIONS_KEY = 'role_permission_snapshot';

// Cache duration: 10 minutes
const CACHE_TTL = 10 * 60 * 1000;

export interface StoredPermissionData {
  permissionGroups: PermissionGroup[];
  userPermissions: UserPermission[];
  userId: string;
  teamIds: string[];
  timestamp: number;
  permissionConfig?: PermissionConfigData | null;
  rolePermissions?: RolePermissionAssignment | null;
}

class PermissionStorageService {
  /**
   * Save permissions to cache (valid for 10 minutes)
   */
  savePermissions(data: {
    permissionGroups: PermissionGroup[];
    userPermissions: UserPermission[];
    userId: string;
    teamIds: string[];
    permissionConfig?: PermissionConfigData | null;
    rolePermissions?: RolePermissionAssignment | null;
  }): void {
    try {
      const timestamp = Date.now();

      localStorage.setItem(PERMISSION_GROUPS_KEY, JSON.stringify(data.permissionGroups));
      localStorage.setItem(USER_PERMISSIONS_KEY, JSON.stringify(data.userPermissions));
      localStorage.setItem(PERMISSION_USER_ID_KEY, data.userId);
      localStorage.setItem(PERMISSION_TEAM_IDS_KEY, JSON.stringify(data.teamIds));
      localStorage.setItem(PERMISSION_TIMESTAMP_KEY, timestamp.toString());
      if (data.permissionConfig !== undefined) {
        localStorage.setItem(PERMISSION_CONFIG_KEY, JSON.stringify(data.permissionConfig));
      }
      if (data.rolePermissions !== undefined) {
        localStorage.setItem(ROLE_PERMISSIONS_KEY, JSON.stringify(data.rolePermissions));
      }

      console.log('[PermissionStorageService] Permissions cached for 10 minutes');
    } catch (error) {
      console.error('[PermissionStorageService] Failed to save permissions:', error);
    }
  }

  /**
   * Load cached permissions (valid for 10 minutes)
   */
  loadPermissions(): StoredPermissionData | null {
    try {
      const timestamp = localStorage.getItem(PERMISSION_TIMESTAMP_KEY);
      const userId = localStorage.getItem(PERMISSION_USER_ID_KEY);
      const permissionGroupsStr = localStorage.getItem(PERMISSION_GROUPS_KEY);
      const userPermissionsStr = localStorage.getItem(USER_PERMISSIONS_KEY);
      const teamIdsStr = localStorage.getItem(PERMISSION_TEAM_IDS_KEY);
      const permissionConfigStr = localStorage.getItem(PERMISSION_CONFIG_KEY);
      const rolePermissionsStr = localStorage.getItem(ROLE_PERMISSIONS_KEY);

      // Check if all required data exists
      if (!timestamp || !userId || !permissionGroupsStr || !userPermissionsStr) {
        console.debug('[PermissionStorageService] No cached permissions available');
        return null;
      }

      // Check if cache has expired
      const dataAge = Date.now() - parseInt(timestamp);
      if (dataAge > CACHE_TTL) {
        console.debug('[PermissionStorageService] Cached permissions expired, clearing');
        this.clearPermissions();
        return null;
      }

      // Parse cached data
      const permissionGroups = JSON.parse(permissionGroupsStr);
      const userPermissions = JSON.parse(userPermissionsStr);
      const teamIds = teamIdsStr ? JSON.parse(teamIdsStr) : [];
      const permissionConfig = permissionConfigStr ? JSON.parse(permissionConfigStr) : null;
      const rolePermissions = rolePermissionsStr ? JSON.parse(rolePermissionsStr) : null;

      console.log(
        '[PermissionStorageService] Loaded cached permissions (age: ' +
          Math.round(dataAge / 1000) +
          's)',
      );

      return {
        permissionGroups,
        userPermissions,
        userId,
        teamIds,
        timestamp: parseInt(timestamp),
        permissionConfig,
        rolePermissions,
      };
    } catch (error) {
      console.error('[PermissionStorageService] Failed to load cached permissions:', error);
      this.clearPermissions();
      return null;
    }
  }

  /**
   * Legacy method for temporary storage (keep for compatibility)
   */
  saveTemporaryPermissions(data: {
    permissionGroups: PermissionGroup[];
    userPermissions: UserPermission[];
    userId: string;
    teamIds: string[];
    permissionConfig?: PermissionConfigData | null;
    rolePermissions?: RolePermissionAssignment | null;
  }): void {
    // Just use the main cache storage
    this.savePermissions(data);
  }

  /**
   * Legacy method for temporary loading (keep for compatibility)
   */
  loadTemporaryPermissions(): StoredPermissionData | null {
    return this.loadPermissions();
  }

  /**
   * Clear all cached permission data
   */
  clearPermissions(): void {
    localStorage.removeItem(PERMISSION_GROUPS_KEY);
    localStorage.removeItem(USER_PERMISSIONS_KEY);
    localStorage.removeItem(PERMISSION_USER_ID_KEY);
    localStorage.removeItem(PERMISSION_TEAM_IDS_KEY);
    localStorage.removeItem(PERMISSION_TIMESTAMP_KEY);
    localStorage.removeItem(PERMISSION_CONFIG_KEY);
    localStorage.removeItem(ROLE_PERMISSIONS_KEY);
    console.log('[PermissionStorageService] Cached permissions cleared');
  }

  /**
   * Legacy method (keep for compatibility)
   */
  clearTemporaryPermissions(): void {
    this.clearPermissions();
  }

  /**
   * Check if valid cached permissions exist
   */
  hasPermissions(): boolean {
    return this.loadPermissions() !== null;
  }

  /**
   * Update user permissions in cache
   */
  updateUserPermissions(userPermissions: UserPermission[]): void {
    const cachedData = this.loadPermissions();
    if (cachedData) {
      this.savePermissions({
        ...cachedData,
        userPermissions,
      });
      console.log('[PermissionStorageService] Updated user permissions in cache');
    }
  }

  /**
   * Check if cache needs refresh (older than 10 minutes)
   */
  needsRefresh(): boolean {
    const cachedData = this.loadPermissions();
    return cachedData === null; // Need refresh if no valid cache
  }
}

// Create singleton instance
export const permissionStorageService = new PermissionStorageService();

// Clear permissions on logout
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    // Clear permissions if token is removed (logout)
    if (e.key === 'token' && !e.newValue) {
      permissionStorageService.clearPermissions();
    }
  });
}
